import styled from "styled-components/macro";
import type { Process } from "types/types";
import {
  BREAKPOINT_TABLET,
  PNL_COLORS,
  PNL_TEXT_COLORS,
} from "constants/constants";
import { useLatestSegmentForProcess } from "components/Dashboard/Tiles/ProcessTile/ExpandedTile/expandedTileUtils";
import { TooltipStorybookFriendly } from "components/Dashboard/Timeline/useTooltipStorybookFriendly";
import { useSelector } from "store/useSelector";

/** if the process has a callTarget, show the Call Indicator (hand) icon */
export default function CallIndicatorMachineBreakdownRow({
  process,
}: {
  process: Process;
}) {
  const latestSegment = useLatestSegmentForProcess(process.id);
  const segmentColor = latestSegment
    ? PNL_COLORS[latestSegment.pnl.category]
    : "unset";
  const fill = latestSegment
    ? PNL_TEXT_COLORS[latestSegment.pnl.category]
    : "none";
  const dataDump = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess[process.id]
  );
  const callTarget = dataDump?.lastSegment?.callTarget; // TODO
  return callTarget ? (
    <TooltipStorybookFriendly title={callTarget}>
      <CallIndicatorMachineBreakdownRowStyles $segmentColor={segmentColor}>
        <CallIconMachineBreakdownRow {...{ fill }} />
      </CallIndicatorMachineBreakdownRowStyles>
    </TooltipStorybookFriendly>
  ) : null;
}

const CALL_ICON_WIDTH = 15;

const CallIndicatorMachineBreakdownRowStyles = styled.div<{
  $segmentColor: string;
}>`
  position: absolute;
  left: ${-CALL_ICON_WIDTH - 2}px;
  background-color: ${props => props.$segmentColor};
  width: ${CALL_ICON_WIDTH}px;
  height: 22px;
  border-top-right-radius: 999px; /* 50% makes an oval; we want a pill shape here */
  border-bottom-right-radius: 999px; /* 50% makes an oval; we want a pill shape here */

  /* above Tablet, turn the half-pill shape into a full circle */
  @media (min-width: ${BREAKPOINT_TABLET}px) {
    left: ${-CALL_ICON_WIDTH}px;
    border-radius: 999px;
    width: ${CALL_ICON_WIDTH * 2}px;
    height: ${CALL_ICON_WIDTH * 2}px;
    svg {
      transform: translateY(2px) scale(1.4);
      transform-origin: top;
    }
  }
`;

function CallIconMachineBreakdownRow({ fill }: { fill: string }) {
  return (
    <svg
      width="8"
      height="13"
      viewBox="0 0 8 13"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.78707 9.42416C6.77234 9.38365 6.77224 9.34928 6.75024 9.32289C6.57371 8.93989 6.09074 8.77171 5.65992 8.95154C5.26555 9.09517 4.87117 9.23879 4.49144 9.38855C4.41117 9.44072 4.30143 9.41187 4.25005 9.33883C4.12529 9.16636 3.98598 9.02212 3.86859 8.8699C3.36198 8.09101 2.98673 7.22987 2.77949 6.31902C2.6686 5.87771 2.6088 5.40633 2.63674 4.9374C2.62918 4.8484 2.68744 4.76983 2.78245 4.75817C3.16955 4.62867 3.54929 4.47891 3.94366 4.33528C4.4914 4.13581 4.70226 3.72641 4.51808 3.22004C4.41494 2.93648 4.3118 2.65291 4.20866 2.36934C4.09816 2.06552 3.99502 1.78195 3.88451 1.47813C3.88441 1.44376 3.86241 1.41736 3.84767 1.37685C3.69306 0.985875 3.19545 0.811558 2.77926 0.997532C2.38489 1.14116 2.00516 1.29092 1.59614 1.4284C1.23822 1.53581 0.931873 1.78501 0.757267 2.08944C0.473594 2.60563 0.292389 3.16478 0.264743 3.73683C0.143722 4.90485 0.24199 6.06183 0.559449 7.17339C0.943789 8.67348 1.66414 10.0741 2.69094 11.26C3.13842 11.8081 3.69534 12.2819 4.3469 12.6066C4.7716 12.8533 5.31265 12.8742 5.79456 12.6643C6.13775 12.5164 6.51758 12.401 6.86813 12.2733C7.39396 12.0818 7.6121 11.6583 7.43529 11.1722C7.22164 10.5848 7.00072 10.0115 6.78707 9.42416Z"
        fill={fill}
      />
    </svg>
  );
}
